import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, BarChart2, HelpCircle, User, Activity, Shield, Stethoscope, MessageSquare, UserCheck, Lock } from 'lucide-react';
import { useAuth } from './AuthContext';
import api from '../api/client';
import webSocketEventBus from '../services/webSocketEventBus';

/**
 * BottomNav — Mobile bottom navigation bar (hidden on desktop via lg:hidden).
 * Role-aware: shows different tabs per user type.
 * Each tab maps to a real route (no query-param section switching).
 */
const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.role;
  const [badgeCounts, setBadgeCounts] = useState({ total: 0, messages: 0 });
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [doctorCounts, setDoctorCounts] = useState({ patientLog: 0, requests: 0 });

  useEffect(() => {
    const handler = (counts) => setBadgeCounts(counts);
    webSocketEventBus.on('unreadCounts', handler);
    return () => webSocketEventBus.off('unreadCounts', handler);
  }, []);

  useEffect(() => {
    if (role !== 'USER') return;
    const fetchPending = async () => {
      try {
        const res = await api.get('/care-requests/my-requests?status=pending').catch(() => null);
        const count = Array.isArray(res?.data?.data) ? res.data.data.length : (res?.data?.stats?.pending || 0);
        setPendingRequestsCount(count);
      } catch { /* non-fatal */ }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, [role]);

  useEffect(() => {
    if (role !== 'DOCTOR') return;
    const fetchDoctorCounts = async () => {
      try {
        const [symptomsRes, requestsRes] = await Promise.all([
          api.get('/symptoms?status=OPEN&limit=1').catch(() => null),
          api.get('/care-requests/sent-requests?status=pending&limit=1').catch(() => null),
        ]);
        setDoctorCounts({
          patientLog: symptomsRes?.data?.pagination?.total ?? symptomsRes?.data?.total ?? 0,
          requests: Array.isArray(requestsRes?.data?.data)
            ? requestsRes.data.data.filter(r => r.status === 'pending').length
            : 0,
        });
      } catch { /* non-fatal */ }
    };
    fetchDoctorCounts();
    const interval = setInterval(fetchDoctorCounts, 60000);
    return () => clearInterval(interval);
  }, [role]);

  // ── Role-based tabs (max 5 for mobile UX) ──
  const patientTabs = [
    { path: '/dashboard',      icon: Home,          label: 'Today' },
    { path: '/my-health',      icon: Activity,      label: 'My Health' },
    { path: '/care-requests',  icon: UserCheck,     label: 'Requests' },
    { path: '/messages',       icon: MessageSquare, label: 'Messages' },
    { path: '/my-data',        icon: Lock,          label: 'My Data' },
  ];

  const doctorTabs = [
    { path: '/doctor-dashboard', icon: Home,          label: 'Home' },
    { path: '/priority-queue',   icon: Shield,        label: 'Priority' },
    { path: '/care-team',        icon: BarChart2,     label: 'Patients' },
    { path: '/messages',         icon: MessageSquare, label: 'Messages' },
    { path: '/profile',          icon: User,          label: 'Profile' },
  ];

  const adminTabs = [
    { path: '/admin',            icon: Home,          label: 'Dashboard' },
    { path: '/admin/doctors',    icon: Stethoscope,   label: 'Doctors' },
    { path: '/admin/on-call',    icon: Activity,      label: 'On-Call' },
    { path: '/admin/population', icon: BarChart2,     label: 'Analytics' },
    { path: '/profile',          icon: User,          label: 'Profile' },
  ];

  const tabs = role === 'ADMIN' ? adminTabs : role === 'DOCTOR' ? doctorTabs : patientTabs;

  // Simple path-based active matching
  const isActive = (tabPath) => {
    if (tabPath === location.pathname) return true;
    // For nested routes — but /admin should NOT match /admin/doctors
    // Only match child paths for non-root admin paths
    if (tabPath !== '/' && tabPath !== '/admin' && location.pathname.startsWith(tabPath + '/')) return true;
    return false;
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] lg:hidden z-[100] shadow-lg"
      role="navigation"
      aria-label="Bottom navigation"
    >
      <div className="flex items-center justify-around max-w-md mx-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);

          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              aria-current={active ? 'page' : undefined}
              aria-label={tab.label}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all min-w-[60px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
                active
                  ? 'text-primary bg-primary/10'
                  : 'text-slate-500 hover:text-slate-600 hover:bg-slate-50'
              }`}
            >
              <div className="relative">
                <Icon className="w-6 h-6" strokeWidth={active ? 2.5 : 2} />
                {tab.path === '/messages' && badgeCounts.messages > 0 && (
                  <span className="absolute -top-1 -right-2 bg-error text-white text-xs font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-0.5">
                    {badgeCounts.messages > 99 ? '99+' : badgeCounts.messages}
                  </span>
                )}
                {tab.path === '/care-requests' && role === 'USER' && pendingRequestsCount > 0 && (
                  <span className="absolute -top-1 -right-2 bg-amber-500 text-white text-xs font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-0.5">
                    {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
                  </span>
                )}
                {tab.path === '/priority-queue' && doctorCounts.patientLog > 0 && (
                  <span className="absolute -top-1 -right-2 bg-error text-white text-xs font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-0.5">
                    {doctorCounts.patientLog > 99 ? '99+' : doctorCounts.patientLog}
                  </span>
                )}
              </div>
              <span className={`text-xs font-bold ${active ? 'text-primary' : 'text-slate-500'}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
